import { can } from '@misumi/shared'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import misumiAmericasLogoUrl from '../assets/brand/misumi-americas-logo.png'
import misumiAmericasMarkUrl from '../assets/brand/misumi-americas-mark.png'
import { useAuth } from '../lib/auth/auth-context'
import { countOpenDrafts, useRfqBundle } from '../lib/rfq/rfq-bundle-context'
import { useRfqCount } from '../lib/rfq/rfq-count-context'
import { composedVerticals } from '../verticals'
import { useUploadSessions } from '../verticals/uploads/useUploads'
import { NewWorkspaceModal } from '../verticals/workspaces/components/NewWorkspaceModal'
import type { WorkspaceSummary } from '../verticals/workspaces/types'
import { defaultWorkspacesService, useWorkspaceList } from '../verticals/workspaces/useWorkspaces'
import { tintClassForWorkspace } from '../verticals/workspaces/workspace-tint'
import { IconFolderTile, LockOutlined } from './icons'
import { Icon, type IconName } from './ui/Icon'
import { useToast } from './ui/ToastProvider'
import { Tooltip } from './ui/Tooltip'

/**
 * Member-count/role subtitle for a project row — shared verbatim (UID-368
 * AC 2) by the expanded `SidebarWorkspaceItem` row and the collapsed-rail
 * letter-tile tooltip, so the two text strings can never drift apart.
 *
 * UID-367: the comp hard-codes the plural ("members" even for a count of 1
 * — `sub: joined ? (members.length+' members') : 'Not a member'`, `Fictiv
 * Platform.dc.html:8774`), the same hard-coded-plural convention
 * ADR-0114/UID-348 recorded for `WorkspaceCard`'s "N BOMs". Deliberate
 * deviation here: `WorkspaceCard` itself (the sibling "all projects" card)
 * pluralizes CORRECTLY (`member${count === 1 ? '' : 's'}`) — matching that
 * for in-app consistency rather than porting the comp's own inconsistency a
 * second time.
 */
function subtitleForWorkspace(workspace: WorkspaceSummary): string {
  return workspace.myRole
    ? `${workspace.memberCount} member${workspace.memberCount === 1 ? '' : 's'}`
    : 'Not a member'
}

// Icon per nav item id — the vertical registry (../verticals) only knows
// label/path/capability, not visual iconography (it's a route-registration
// contract, not a design one, see verticals/types.ts), so the shell owns
// this presentation-only mapping. Keyed by the vertical module id, falling
// back to a generic glyph for anything the map doesn't recognize so a new
// vertical never renders with no icon at all.
//
// UID-136: 'catalog' and 'parts' used to share ONE Ant glyph each with
// 'search'/'rfq' respectively — the design source draws four DISTINCT
// pictograms for these four nav items (grid / file-text / box / package),
// so this map now uses four distinct `IconName`s instead of two reused ones.
const VERTICAL_ICON: Record<string, IconName> = {
  catalog: 'catalog',
  parts: 'library',
  rfq: 'document',
  orders: 'shopping-bag',
  uploads: 'upload',
}

export interface SidebarProps {
  readonly collapsed: boolean
  readonly onToggleCollapsed: () => void
  /** ≤900px off-canvas drawer state (owned by AppShell). Optional so the
   * many presentational Sidebar tests that predate the drawer keep working
   * (defaults: closed, no-op close). */
  readonly mobileOpen?: boolean
  readonly onCloseMobile?: () => void
}

/**
 * Persistent left nav (components.md "Sidebar"; UID-27 global-chrome
 * requirement 1). Expanded (240px, `--sidebar-width-expanded`) or collapsed
 * to an icon rail (64px, `--sidebar-width-collapsed`) — see tokens.md §9 for
 * why those exact widths are a resolved-not-measured placeholder
 * (open-questions.md #9). Every nav item keeps its text label in the DOM
 * even when collapsed (visually hidden, not removed) so the accessible name
 * never changes across the collapse transition, and wraps it in a
 * `<Tooltip>` (interactions.md §8) for sighted mouse/keyboard users.
 *
 * Nav items beyond "Home" come from the vertical registry
 * (`composedVerticals.navItems`) exactly as the pre-UID-27 `AppShell` nav
 * did — this component only changes WHERE that list renders, not the
 * capability-gating logic that decides WHICH items are in it.
 */
export function Sidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen = false,
  onCloseMobile,
}: SidebarProps) {
  const auth = useAuth()
  const location = useLocation()
  const { showToast } = useToast()
  // UID-529 (ADR-0167, amended) — supersedes the UID-112 nav-badge
  // semantics: the "RFQ" nav item shows the number of RFQs a customer can
  // actually SEE on the `/rfq` list page, not a raw backend/DB total.
  // `useHydrateRfqCount` (called from `<AppShell>`) hydrates the SUBMITTED
  // count (every server status except `draft` — see that hook's own doc
  // comment for why `draft` rows are excluded here); `totalCount` is
  // `undefined` until that resolves (or if `<RfqCountProvider>` isn't
  // mounted — this hook's inert fallback), so the badge default (0) below
  // simply hides until then rather than showing a wrong number.
  //
  // UID-551 (ADR-0172 D4) — "kill the boolean". The list page's own Draft
  // tab is NOT server data — it renders one card per CLIENT SLOT the
  // session is holding (`RfqListPage.tsx`), so this badge's draft addend
  // must be the real slot count, not a 0/1 presence check. It reads
  // `countOpenDrafts(rfqBundle.slots)` — the SAME shared seam
  // `RfqListPage.tsx`'s own Draft-tab badge reads — so the two can never
  // independently drift (D4's hard constraint: do not inline
  // `rfqBundle.slots.length` here instead, and do not recompute this from
  // `GET /rfq`'s `draft`-status rows — see `useHydrateRfqCount`'s doc
  // comment for why those stay excluded).
  const { totalCount } = useRfqCount()
  const rfqBundle = useRfqBundle()
  const rfqTotalCount = (totalCount ?? 0) + countOpenDrafts(rfqBundle.slots)

  // UID-245 (2026-08-23 design refresh, README §5): a numeric badge on the
  // Uploads nav item while a session is still in progress server-side
  // ('uploading'/'identifying' — not yet 'ready'/'confirmed'). `useUploadSessions`
  // fetches once per mount with no built-in polling; Sidebar is long-lived
  // (mounted once for the whole app shell), so it explicitly `refetch()`es on
  // every route change — the natural point a user is likely to glance at the
  // sidebar again — rather than inventing a timer-polling pattern nothing
  // else in this app uses.
  const { state: uploadSessionsState, refetch: refetchUploadSessions } = useUploadSessions()
  // UID-258 code-review follow-up: the "Projects" slot below used to be a
  // deliberate stub (no workspace-listing API existed yet). That API and its
  // consumer pattern (AllWorkspacesPage.tsx) now exist, so this wires the
  // real list in — same `useWorkspaceList`/`defaultWorkspacesService` seam,
  // no new service call invented.
  const { state: workspacesState, refetch: refetchWorkspaces } =
    useWorkspaceList(defaultWorkspacesService)
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false)
  // Only re-run on navigation (pathname change) — refetchUploadSessions is
  // stable (useCallback) and deliberately excluded so this doesn't refire on
  // every render, only on route changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    refetchUploadSessions()
  }, [location.pathname])
  const uploadsInProgressCount =
    uploadSessionsState.status === 'ready'
      ? uploadSessionsState.sessions.filter(
          (s) => s.status === 'uploading' || s.status === 'identifying',
        ).length
      : 0

  const navItems: ReadonlyArray<{ label: string; to: string; icon: IconName }> = [
    { label: 'Home', to: '/', icon: 'home' },
    ...(auth.status === 'authenticated'
      ? composedVerticals.navItems
          .filter(
            (item) =>
              item.requiredCapability === undefined ||
              can(auth.session.accountContext.role, item.requiredCapability),
          )
          .map((item) => ({
            label: item.label,
            to: item.to,
            icon: VERTICAL_ICON[item.to.replace('/', '')] ?? 'document',
          }))
      : []),
  ]

  // UID-301: the authoritative prototype (Fictiv Platform.dc.html:232–239)
  // draws the nav as TWO divider-separated groups in a fixed order that does
  // NOT match the vertical-registry array order — so the Sidebar (not the
  // registry) owns the ordering/grouping here, while the registry still owns
  // WHICH items exist (the capability-gating above). Items are looked up by
  // path; any that's absent (gated out, or an as-yet-unknown future vertical)
  // is simply skipped or appended, so a nav item can never silently vanish.
  const navByPath = new Map(navItems.map((item) => [item.to, item]))
  const PRIMARY_ORDER = ['/', '/catalog', '/rfq', '/orders']
  const SECONDARY_ORDER = ['/uploads', '/parts']
  const pick = (order: readonly string[]) =>
    order
      .map((to) => navByPath.get(to))
      .filter((item): item is (typeof navItems)[number] => item !== undefined)
  const known = new Set([...PRIMARY_ORDER, ...SECONDARY_ORDER])
  // Unknown/future items ride with the primary group so they never end up
  // orphaned below the divider (and never leave a dangling empty divider).
  const primaryGroup = [...pick(PRIMARY_ORDER), ...navItems.filter((item) => !known.has(item.to))]
  const secondaryGroup = pick(SECONDARY_ORDER)

  const className = [
    'sidebar',
    collapsed ? 'sidebar-collapsed' : '',
    mobileOpen ? 'sidebar-mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  // One nav row — shared by both groups (UID-301). Collapsed rows wrap in a
  // <Tooltip> (interactions.md §8); the label always stays in the DOM so the
  // accessible name never changes across the collapse transition.
  function renderNavItem(item: (typeof navItems)[number]) {
    const active = location.pathname === item.to
    // UID-529: hidden at 0 — a brand-new zero-RFQ account shows no badge at
    // all, same hide-at-0 rule the total-count semantic still wants (an
    // empty badge is just visual noise). Capped at "99+" purely for sidebar
    // layout width, same as before — both carry over unchanged from the
    // superseded UID-112 in-progress-count semantic (ADR-0167).
    const showRfqBadge = item.to === '/rfq' && rfqTotalCount > 0
    // UID-529 follow-up: the RFQ badge used to override to purple
    // (`sidebar-nav-badge-purple`, UID-245) to visually distinguish the
    // bundle's in-progress count from the Uploads badge. Now that it's a
    // total-record count rather than an in-progress count, that distinction
    // no longer applies — both nav items share the same default
    // `.sidebar-nav-badge` indigo fill (`Fictiv Platform.dc.html:238`).
    const showUploadsBadge = item.to === '/uploads' && uploadsInProgressCount > 0
    const badgeCount = showRfqBadge ? rfqTotalCount : uploadsInProgressCount
    const link = (
      <Link
        key={item.to}
        to={item.to}
        className={active ? 'sidebar-nav-item sidebar-nav-item-active' : 'sidebar-nav-item'}
        // Dismiss the mobile drawer on any nav pick. The route-change effect in
        // AppShell already closes it when the path changes; this also covers
        // tapping the current route (no path change).
        onClick={onCloseMobile}
        // The badge is `aria-hidden` (decorative digits, not a second label) —
        // the count is folded into the link's own accessible name, same pattern
        // as TopBar's cart badge, so assistive tech announces it without a
        // second node.
        aria-label={
          showRfqBadge
            ? `${item.label}, ${rfqTotalCount} RFQ${rfqTotalCount === 1 ? '' : 's'}`
            : showUploadsBadge
              ? `${item.label}, ${uploadsInProgressCount} upload${uploadsInProgressCount === 1 ? '' : 's'} in progress`
              : undefined
        }
      >
        <Icon name={item.icon} />
        <span className={collapsed ? 'sr-only' : 'sidebar-nav-label'}>{item.label}</span>
        {(showRfqBadge || showUploadsBadge) && (
          <span className="sidebar-nav-badge" aria-hidden="true">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </Link>
    )
    return collapsed ? (
      <Tooltip key={item.to} label={item.label}>
        {link}
      </Tooltip>
    ) : (
      link
    )
  }

  return (
    <aside className={className}>
      <div className="sidebar-brand">
        {/* UID-389 followup: the mark/logo link and collapse toggle share one
            grid cell when collapsed (design source: `.fx-sblogo`/`.fx-sblogo-btn`
            in Fictiv Platform.dc.html:226-228) so they sit in the exact same
            spot instead of stacking with a gap — hovering anywhere over that
            spot reveals the toggle, with no dead zone to cross to reach it. */}
        <div className="sidebar-brand-mark-slot">
          <Link to="/" className="sidebar-brand-link" aria-label="MISUMI AMERICAS — Home">
            {collapsed ? (
              <img src={misumiAmericasMarkUrl} alt="" className="sidebar-brand-mark" />
            ) : (
              <img src={misumiAmericasLogoUrl} alt="" className="sidebar-brand-logo" />
            )}
          </Link>
          <button
            type="button"
            className="sidebar-collapse-toggle"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={onToggleCollapsed}
          >
            {/* UID-136: the design source rotates the SAME sidebar-panel glyph
                180deg for the expand state rather than using two different
                icons — matched here with a CSS transform, not two glyphs. */}
            <span
              style={
                collapsed ? { display: 'inline-flex', transform: 'rotate(180deg)' } : undefined
              }
            >
              <Icon name="sidebar-collapse" />
            </span>
          </button>
        </div>
        {/* Mobile-only drawer close (≤900px). The collapse toggle above is a
            desktop icon-rail control that makes no sense for an off-canvas
            drawer, so on mobile this explicit ✕ is the in-drawer dismiss (the
            scrim + Escape also close it). Hidden on desktop via shell.css. */}
        <button
          type="button"
          className="sidebar-mobile-close"
          aria-label="Close navigation"
          onClick={onCloseMobile}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <nav aria-label="Primary" className="sidebar-nav">
        {primaryGroup.map(renderNavItem)}
        {/* UID-301: the prototype (Fictiv Platform.dc.html:237) splits the nav
            into two groups with a 1px divider — Home/Search catalog/RFQ/Orders
            above, Uploads/Part library below. Only drawn when the lower group
            actually has (capability-gated) items. */}
        {secondaryGroup.length > 0 && <div className="sidebar-nav-divider" aria-hidden="true" />}
        {secondaryGroup.map(renderNavItem)}
      </nav>

      {!collapsed && auth.status === 'authenticated' && (
        <>
          {/* UID-389 followup: reuses `.sidebar-nav-divider` (height/color) but
              needs a bigger horizontal margin than the nav-group divider
              above — that one sits inside `.sidebar-nav`'s own 12px
              horizontal padding (12px nav padding + 4px divider margin =
              16px inset from the sidebar edge), while this one renders
              directly under `<aside>` with no surrounding padding, so it
              needs the full 16px in its own margin to land at the same
              inset (matches Fictiv Platform.dc.html:243, `margin:8px 16px`
              vs. the nav divider's `margin:8px 4px` at line 237). */}
          <div className="sidebar-nav-divider sidebar-nav-divider--workspaces" aria-hidden="true" />
          <div className="sidebar-workspaces">
            <div className="sidebar-workspaces-header">
              <span className="sidebar-workspaces-title">Projects</span>
              <button
                type="button"
                className="sidebar-workspaces-add"
                aria-label="New project"
                onClick={() => setNewWorkspaceOpen(true)}
              >
                <Icon name="plus" />
              </button>
            </div>
            {workspacesState.status === 'ready' && workspacesState.workspaces.length === 0 && (
              <p className="sidebar-workspaces-empty caption-text">
                Your projects will appear here once you join or create one.
              </p>
            )}

            {workspacesState.status === 'error' && (
              <p className="sidebar-workspaces-empty caption-text">Unable to load projects.</p>
            )}

            {workspacesState.status === 'ready' && workspacesState.workspaces.length > 0 && (
              <ul className="sidebar-workspaces-list">
                {workspacesState.workspaces.map((workspace) => (
                  <li key={workspace.id}>
                    <SidebarWorkspaceItem workspace={workspace} onCloseMobile={onCloseMobile} />
                  </li>
                ))}
              </ul>
            )}

            {/* UID-301 (Fictiv Platform.dc.html:261): the "All projects →" link
              into the full workspaces list. Present regardless of list state —
              it's the entry point to /projects (AllWorkspacesPage). */}
            <Link to="/projects" className="sidebar-all-projects" onClick={onCloseMobile}>
              All projects <span aria-hidden="true">→</span>
            </Link>

            <NewWorkspaceModal
              open={newWorkspaceOpen}
              onClose={() => setNewWorkspaceOpen(false)}
              onCreate={async (request) => {
                try {
                  await defaultWorkspacesService.createWorkspace(request)
                  refetchWorkspaces()
                } catch (error) {
                  // UID-372 — surface the failure. `NewWorkspaceModal` swallows the
                  // rejection by design (its own comment declares that the CALLER
                  // toasts), so without this a duplicate-name 4xx or a network drop
                  // left the user with a re-enabled button and no explanation at all.
                  showToast(
                    error instanceof Error
                      ? error.message
                      : 'Could not create the project. Try again.',
                    'error',
                  )
                  // Rethrow so `NewWorkspaceModal.handleSubmit`'s catch still sees a
                  // failure and keeps the modal open for a retry, instead of treating
                  // this as a successful create and closing.
                  throw error
                }
              }}
            />
          </div>
        </>
      )}

      {/* UID-368 (Fictiv Platform.dc.html:263-276): collapsed-rail variant of
          the Projects mini-list — a column of 40x40 letter tiles, one per
          joined project. The comp shows NONE of the expanded rail's
          "Projects" header, "+" new-project button, "All projects →" link,
          empty-state copy, or error copy in this collapsed block, so those
          are all omitted here; only render anything once there's at least
          one project to show. The comp's "Upload parts or a BOM" tile
          (L266-269) is OUT OF SCOPE for this ticket — its body is empty in
          the comp (no visible tile, tooltip-only), so nothing to port. */}
      {collapsed &&
        auth.status === 'authenticated' &&
        workspacesState.status === 'ready' &&
        workspacesState.workspaces.length > 0 && (
          <>
            <div className="sidebar-workspaces-collapsed-divider" aria-hidden="true" />
            <div className="sidebar-workspaces-collapsed">
              {workspacesState.workspaces.map((workspace) => (
                <SidebarWorkspaceCollapsedTile
                  key={workspace.id}
                  workspace={workspace}
                  onCloseMobile={onCloseMobile}
                />
              ))}
            </div>
          </>
        )}

      {/* Push the help card to the very bottom of the rail (prototype's
          `flex:1` spacer, Fictiv Platform.dc.html:278). */}
      <div className="sidebar-footer-spacer" />

      {/* UID-301 (Fictiv Platform.dc.html:280–295): the "Misumi team is here to
          help" contact card. Expanded rail only — the collapsed rail has no
          room for it. */}
      {!collapsed && (
        <div className="sidebar-help-card">
          {/* Three team faces (Fictiv Platform.dc.html:280–285). Real
              headshots, supplied for this card — decorative (the card's
              text carries the meaning), so aria-hidden and empty alt. */}
          <div className="sidebar-help-avatars" aria-hidden="true">
            {HELP_FACE_IMAGES.map((src) => (
              <span key={src} className="sidebar-help-avatar">
                <img src={src} alt="" />
              </span>
            ))}
          </div>
          <p className="sidebar-help-title">
            The Misumi team is here to help with quoting and procurement
          </p>
          <div className="sidebar-help-contact">
            <span className="sidebar-help-contact-label caption-text">
              General questions &amp; quoting
            </span>
            <div className="sidebar-help-contact-row">
              <a href="mailto:sales@misumi.com" className="sidebar-help-email">
                sales@misumi.com
              </a>
              <button
                type="button"
                className="sidebar-help-copy"
                aria-label="Copy sales@misumi.com"
                onClick={() => copyEmail('sales@misumi.com')}
              >
                <Icon name="copy" />
              </button>
            </div>
          </div>
          <div className="sidebar-help-contact">
            <span className="sidebar-help-contact-label caption-text">Order questions</span>
            <div className="sidebar-help-contact-row">
              <a href="mailto:support@misumi.com" className="sidebar-help-email">
                support@misumi.com
              </a>
              <button
                type="button"
                className="sidebar-help-copy"
                aria-label="Copy support@misumi.com"
                onClick={() => copyEmail('support@misumi.com')}
              >
                <Icon name="copy" />
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

/**
 * One row in the sidebar's "Projects" mini-list (UID-367,
 * `Fictiv Platform.dc.html:251-258`) — a tinted folder-icon swatch, the
 * project name + member-count subtitle, and a defensive lock affordance.
 * Extracted out of `Sidebar` since the parent was already large and the
 * ticket itself proposes this split.
 *
 * The `<Link>` carries an explicit `aria-label` set to the bare project
 * name — the subtitle text and (when present) the lock icon's own
 * `aria-label` sit inside as VISIBLE content, but an explicit `aria-label`
 * on the link wins the accessible-name computation outright, so neither one
 * leaks into the link's announced name (WCAG 4.1.2). The folder-tile icon is
 * `aria-hidden` (purely decorative); the lock icon is NOT — it gets its own
 * meaningful label, same pattern/wording as `WorkspaceCard`'s lock.
 */
function SidebarWorkspaceItem({
  workspace,
  onCloseMobile,
}: {
  readonly workspace: WorkspaceSummary
  readonly onCloseMobile: (() => void) | undefined
}) {
  // UID-367: the comp's own `wsSidebar` mapper (Fictiv Platform.dc.html:8772)
  // filters to `wsJoined` projects ONLY before this markup ever renders, so
  // its `notJoined` branch (L257) is structurally dead in the comp itself.
  // Ported anyway (the ticket AC asks for the markup, and it's the SAME
  // defensive affordance `WorkspaceCard` keeps for the same reason — see its
  // docblock): per the frozen `GET /api/workspaces` contract (types.ts), a
  // private workspace the caller isn't a member of is simply absent from
  // this list, never present-with-a-lock — so this condition shouldn't be
  // reachable today, only guards against a future contract change.
  const isLockedPrivate = workspace.visibility === 'private' && workspace.myRole === null

  // UID-368: extracted to the module-level `subtitleForWorkspace` helper
  // (see its doc comment for the pluralization rationale) so this text can
  // never drift from the collapsed-rail tile's tooltip.
  const subtitle = subtitleForWorkspace(workspace)

  return (
    <Link
      to={`/projects/${workspace.id}`}
      className="sidebar-workspaces-item"
      aria-label={workspace.name}
      onClick={onCloseMobile}
    >
      <span
        className={`sidebar-workspaces-icon ${tintClassForWorkspace(workspace.id)}`}
        aria-hidden="true"
      >
        <IconFolderTile size={16} />
      </span>
      <span className="sidebar-workspaces-text">
        <span className="sidebar-workspaces-name">{workspace.name}</span>
        <span className="sidebar-workspaces-sub">{subtitle}</span>
      </span>
      {/* UID-367 code-review (W1) glyph-consistency note: `IconFolderTile`
       * above is a hand-inlined verbatim-path SVG *specifically because*
       * `@ant-design/icons`' `FolderOutlined` is a different silhouette
       * from the comp's open-folder glyph. `LockOutlined` below is the
       * SAME kind of mismatch — it's a FILLED lock on a `64 64 896 896`
       * viewBox, not the comp's stroked outline (L257) — but is kept
       * anyway rather than given the same inline-SVG treatment: it's the
       * exact same deviation `WorkspaceCard.tsx` already ships (consistency
       * with that sibling component), and the branch is unreachable against
       * the real contract today (see the `isLockedPrivate` comment above),
       * so the visual mismatch is immaterial. Sizing: `LockOutlined` is a
       * bare antd re-export — it renders `<span class="anticon"><svg
       * width="1em" height="1em">`, and antd's own icons size via
       * `font-size` on the wrapper, not `width`/`height` (antd's CSS reset
       * that would make a `width`/`height` override matter isn't loaded —
       * `antd` proper isn't a dependency here) — so `.sidebar-workspaces-
       * lock`'s `font-size: 13px` in shell.css is what actually sizes this
       * glyph; a `width`/`height` rule the way `.sidebar-workspaces-add
       * svg` does it would land on the wrong element and be a no-op. */}
      {isLockedPrivate && (
        <LockOutlined
          aria-label="Private — you are not a member"
          title="Private project"
          className="sidebar-workspaces-lock"
        />
      )}
    </Link>
  )
}

/**
 * One tile in the collapsed rail's Projects letter-tile column (UID-368,
 * `Fictiv Platform.dc.html:272-273`) — a 40x40 tinted square with the
 * project's first-letter initial, tooltipped with the same name/subtitle
 * the expanded row shows.
 *
 * Tint parity (AC 3): reuses `tintClassForWorkspace(workspace.id)` — the
 * SAME function/input the expanded row's icon swatch uses — applied
 * directly to this tile (the `<Link>` itself, no extra wrapper span).
 *
 * CONTRAST (code-review B1): the letter is real 15px/600 TEXT and the SOLE
 * visual identifier of the project — unlike the expanded row's `aria-hidden`
 * folder glyph, which sits beside the row's own visible project-name text,
 * this glyph has no such neighbor. So WCAG 1.4.3 (4.5:1) governs here, not
 * 1.4.11's decorative exemption, and the raw tint fg fails it on 3 of 4
 * tints — `shell.css`'s `.workspace-tint-{1,2,3}
 * .sidebar-workspaces-collapsed-letter` overrides `color` to the darkened
 * `-text` token for those three (tint-0 already clears 4.5:1 unmodified).
 * `color` still inherits from the tint class for the DEFAULT (tint-0) case;
 * it is NOT free for the other three.
 *
 * Non-joined projects (defensively ported, same as `SidebarWorkspaceItem` —
 * see its `isLockedPrivate` comment for why this is structurally unreachable
 * against today's contract) get a tile with a "Name · Not a member" tooltip
 * and NO lock affordance — the collapsed rail has no room for a second
 * glyph per tile, so this asymmetry with the expanded row's lock icon is a
 * recorded choice, not an oversight.
 *
 * Accessibility: an explicit `aria-label` of the bare project name (same
 * pattern as `SidebarWorkspaceItem`) wins the accessible-name computation
 * over the decorative letter glyph, and `Tooltip` clones the single `<Link>`
 * child to wire up `aria-describedby`.
 */
function SidebarWorkspaceCollapsedTile({
  workspace,
  onCloseMobile,
}: {
  readonly workspace: WorkspaceSummary
  readonly onCloseMobile: (() => void) | undefined
}) {
  const subtitle = subtitleForWorkspace(workspace)
  // Comp mapper (Fictiv Platform.dc.html:8774): `(p.name||'?').trim().charAt(0).toUpperCase()`.
  // That `||` only catches a fully-empty name — a whitespace-only name
  // (e.g. "   ") is truthy, survives to `.trim()`, and yields '' rather than
  // '?'. The second `|| '?'` below closes that gap, but — same shape as the
  // `isLockedPrivate` note above — is unreachable against today's contract:
  // the API service trims and rejects an empty/whitespace-only name on both
  // create and update (`workspace-service.ts:343,377`, "defense-in-depth
  // beyond ajv"), and `NewWorkspaceModal.tsx` also sends `name.trim()`. Kept
  // as a guard against a future contract change, not a live code path.
  const letter = (workspace.name || '?').trim().charAt(0).toUpperCase() || '?'

  return (
    <Tooltip placement="right" label={`${workspace.name} · ${subtitle}`}>
      <Link
        to={`/projects/${workspace.id}`}
        className={`sidebar-workspaces-collapsed-tile ${tintClassForWorkspace(workspace.id)}`}
        aria-label={workspace.name}
        onClick={onCloseMobile}
      >
        <span className="sidebar-workspaces-collapsed-letter" aria-hidden="true">
          {letter}
        </span>
      </Link>
    </Tooltip>
  )
}

/** Best-effort copy of a support email to the clipboard (UID-301 help card).
 * Guarded because `navigator.clipboard` is undefined in insecure contexts and
 * in the jsdom test environment — a missing clipboard is a silent no-op, never
 * a thrown error in the render path. */
function copyEmail(email: string) {
  void navigator.clipboard?.writeText(email)
}

/** Real headshots for the help-card team faces (UID-301 follow-up) — replaces
 * the earlier illustrated-SVG placeholders now that photos have been
 * supplied (`apps/web/public/assets/expert-{1,2,3}.webp`). */
const HELP_FACE_IMAGES: readonly string[] = [
  '/assets/expert-1.webp',
  '/assets/expert-2.webp',
  '/assets/expert-3.webp',
]
